import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { asyncActions } from './asyncActions';
import { WarehouseLocationsGroupedModel } from '@/Models/Logistics/LogisticModel';
import { CountryCode, LogisticType } from '@/Enums';
import { ModalDataType } from '@/Types/ModalDataType';
import { LogisticInfoModel } from '@/Models/Logistics/LogisticInfoModel';
import { LogisticShortModel } from '@/Models/Logistics/LogisticShortModel';
import { AvailableLogisticPairModel } from '@/Models/Logistics/AvailableLogisticPairModel';
import { UserProductTypeModel } from '@/Models/UserProducts/UserProductTypeModel';
import { Pair } from '@/Types';

type ContractModalType = {
  visible: boolean;
  logisticType?: LogisticType;
  logisticTypeId?: number;
  contractId?: number;
  locationId?: number;
  mandatId?: number;
  country?: CountryCode;
  contract?: any;
};

type DeleteModalType = {
  name: string;
  callback: () => void;
};

type ConnectModalType = {
  visible: boolean;
  connectStep?: 'select' | 'warning';
  locationId?: number;
  mandatId?: number;
  country?: CountryCode;
  isAllTypesConnected?: boolean;
  logisticTypes?: AvailableLogisticPairModel[];
};

type ConfirmConnectModal = {
  contract: any;
  logisticType: LogisticType;
};

export type LogisticSettingsState = {
  groupedLocations: WarehouseLocationsGroupedModel[];
  groupedLogistics: LogisticShortModel[];
  connectModal: ConnectModalType;
  logisticInfoModal: ModalDataType<LogisticInfoModel>;
  logisticContractModal: ContractModalType;
  deleteContractModal: ModalDataType<DeleteModalType>;
  confirmConnectModal: ModalDataType<ConfirmConnectModal>;
  noProductTypesModal: ModalDataType<number>;
  contractActiveStep: number;
  contractUserProducts: UserProductTypeModel[];
  contractLabelFormats: Pair[];
};

const initialState: LogisticSettingsState = {
  groupedLocations: [],
  groupedLogistics: [],
  connectModal: { visible: false },
  logisticInfoModal: { visible: false },
  logisticContractModal: { visible: false },
  deleteContractModal: { visible: false },
  confirmConnectModal: { visible: false },
  noProductTypesModal: { visible: false },
  contractActiveStep: 0,
  contractUserProducts: [],
  contractLabelFormats: [],
};

const logisticSettingsSlice = createSlice({
  name: 'logisticSettings',
  initialState,
  reducers: {
    setContractActiveStep(state, action: PayloadAction<number>) {
      state.contractActiveStep = action.payload;
    },
    setConnectModal(state, action: PayloadAction<ConnectModalType>) {
      state.connectModal = action.payload;
    },
    setConnectModalStep(state, action: PayloadAction<'select' | 'warning'>) {
      state.connectModal.connectStep = action.payload;
    },
    setLogisticInfoModal(state, action: PayloadAction<ModalDataType<LogisticInfoModel>>) {
      state.logisticInfoModal = action.payload;
    },
    setDeleteModal(state, action: PayloadAction<ModalDataType<DeleteModalType>>) {
      state.deleteContractModal = action.payload;
    },
    setContractModal(state, action: PayloadAction<ContractModalType>) {
      state.logisticContractModal = action.payload;
    },
    setConfirmConnectModal(state, action: PayloadAction<ModalDataType<ConfirmConnectModal>>) {
      state.confirmConnectModal = action.payload;
    },
    setNoProductTypesModal(state, action: PayloadAction<ModalDataType<number>>) {
      state.noProductTypesModal = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(asyncActions.getGroupedUserWarehouses.fulfilled, (state, action) => {
        state.groupedLocations = action.payload.getMandatsWarehouses;
      })
      .addCase(asyncActions.getLogisticsByWarehouse.fulfilled, (state, action) => {
        state.groupedLogistics = action.payload.logisticContracts;
      })
      .addCase(asyncActions.getDetailedLogisticInfo.fulfilled, (state, action) => {
        state.logisticInfoModal = { visible: true, data: action.payload };
      })
      .addCase(asyncActions.getAvailableLogistics.fulfilled, (state, action) => {
        const { logistics, allUserLogisticsUsed } = action.payload;
        state.connectModal.logisticTypes = logistics;
        state.connectModal.isAllTypesConnected = allUserLogisticsUsed;
      })
      .addCase(asyncActions.getContractUserProducts.fulfilled, (state, action) => {
        const productTypes = action.payload.userProductTypes;
        state.contractUserProducts = productTypes;

        if (productTypes.length == 0) {
          state.logisticContractModal = { visible: false };
          state.noProductTypesModal = { visible: true, data: state.logisticContractModal.logisticTypeId };
        }
      })
      .addCase(asyncActions.getLogisticContract.fulfilled, (state, action) => {
        state.logisticContractModal.contract = action.payload.contract;
      })
      .addCase(asyncActions.getDeutschePostContract.fulfilled, (state, action) => {
        state.logisticContractModal.contract = action.payload.contract;
      })
      .addCase(asyncActions.getLogisticLabelFormats.fulfilled, (state, action) => {
        state.contractLabelFormats = action.payload.formats.map((item) => ({ key: item.id, value: item.name }));
      })
      .addCase(asyncActions.getDeutschePostLabelFormats.fulfilled, (state, action) => {
        state.contractLabelFormats = action.payload.formats.map((item) => ({ key: item.id, value: item.name }));
      });

    builder.addMatcher(
      (action) =>
        [
          asyncActions.createLogisticContract.fulfilled.type,
          asyncActions.createDeutschePostContract.fulfilled.type,
        ].includes(action.type),
      (state, action) => {
        const contract = action.payload.contract;

        state?.logisticInfoModal?.data?.contracts.push({
          id: contract.id,
          name: contract.name,
          isActive: contract.isActive,
        });
      },
    );

    builder.addMatcher(
      (action) =>
        [
          asyncActions.updateLogisticContractState.fulfilled.type,
          asyncActions.updateDeutschePostContractState.fulfilled.type,
        ].includes(action.type),
      (state, action) => {
        const contractIdx = state.logisticInfoModal.data?.contracts?.findIndex((item) => {
          return item.id === action.payload.id;
        });

        if (contractIdx != undefined && state.logisticInfoModal.data) {
          state.logisticInfoModal.data.contracts[contractIdx].isActive = action.payload.isActive;
        }
      },
    );

    builder.addMatcher(
      (action) =>
        [
          asyncActions.updateLogisticContract.fulfilled.type,
          asyncActions.updateDeutschePostContract.fulfilled.type,
        ].includes(action.type),
      (state, action) => {
        const contract = action.payload.contract;
        const contractIdx = state.logisticInfoModal.data?.contracts.findIndex((item) => {
          return item.id == contract.id;
        });

        if (contractIdx != undefined) {
          state.logisticInfoModal.data!.contracts[contractIdx].name = contract.name;
          state.logisticInfoModal.data!.contracts[contractIdx].isActive = contract.isActive;
        }
      },
    );

    builder.addMatcher(
      (action) =>
        [
          asyncActions.deleteLogisticContract.fulfilled.type,
          asyncActions.deleteDeutschePostContract.fulfilled.type,
        ].includes(action.type),
      (state, action) => {
        const contracts = state.logisticInfoModal?.data?.contracts?.filter((item) => {
          return item.id != action.payload;
        });

        state.logisticInfoModal.data!.contracts = contracts!;
      },
    );
  },
});

const { actions, reducer } = logisticSettingsSlice;

export const logisticSettingsActions = actions;
export const logisticSettingsReducer = reducer;
export const logisticSettingsAsyncActions = asyncActions;
